/**
 * The seating API's refusals, in words a host can act on.
 *
 * Every one of these is a guard doing its job, not a bug, and each has a
 * different next move — swap, reload, force, or pick someone else. Rendering the
 * raw `{ error: 'seat_occupied' }` puts the host in front of a snake_case token
 * and no way forward, which is how a room gets rearranged by hand in a
 * spreadsheet instead.
 */
import { mutationErrorMessage } from './mutation-error';

/** A refusal slug is lower_snake_case; a sentence written for a host never is. */
const SLUG = /^[a-z][a-z0-9_]*$/;

/**
 * The machine slug on a 4xx body, '' when the failure carries none.
 *
 * The seating API answers `{ error: <sentence for the host>, code: <slug> }`
 * (GroupsController.Seating.cs SeatingRefusalAsync, and the same shape on every
 * table and batch refusal). Reading the slug out of `error` returns an English
 * sentence instead, which matches nothing in MESSAGES and quietly unarms every
 * recovery button on the page — including "Seat them anyway", the only way to
 * seat a guest who never claimed a spot.
 *
 * Either key may be missing: the plain validation refusals send `error` alone,
 * and a body may carry only `code`. Take `code` when it is there, and accept
 * `error` only when it reads as a slug rather than prose.
 */
export function seatingErrorCode(error: unknown): string {
  const body = (error as { response?: { data?: { code?: unknown; error?: unknown } } })?.response?.data;
  if (!body || typeof body !== 'object') return '';
  if (typeof body.code === 'string' && body.code.trim()) return body.code.trim();
  const legacy = typeof body.error === 'string' ? body.error.trim() : '';
  return SLUG.test(legacy) ? legacy : '';
}

const MESSAGES: Record<string, string> = {
  version_conflict:
    'Someone else changed this chart while you were arranging it. Reload to pick up their version — your last change was not saved.',
  seat_occupied: 'Somebody is already in that chair. Swap them, or pick an empty one.',
  not_attending:
    'They have not claimed a spot at this dinner. You can seat them anyway if you know they are coming.',
  party_size_exceeded:
    'That guest already has every plus-one chair their dinner claim allows. Raise their party size on the dinner first.',
  seat_index_out_of_range: 'That chair is past the end of this table. Reload — the table changed size.',
  table_has_assignments: 'People are sitting at that table. Deleting it unseats every one of them.',
  duplicate_label: 'A table with that name is already in this room. Two "Table 4"s is a service error waiting to happen.',
  chart_exists_for_dinner:
    'This dinner already has a seating chart. Open that one, or archive it to start over.',
  chart_has_assignments:
    'Guests are already seated on this chart, so it cannot be pointed at a different dinner — that would change who counts as attending underneath them.',
  shape_field_conflict: 'A round table takes one seat count; a rectangle takes a count per side.',
  square_not_symmetric: 'A square renders 1:1, so opposite sides must match. Use a rectangle instead.',
  capacity_out_of_range: 'A table has to seat between 1 and 60. Split a bigger one into two.',
};

/** Known refusal first, then whatever the API said, then the transport error. */
export function seatingErrorMessage(error: unknown): string {
  if (!error) return '';
  return MESSAGES[seatingErrorCode(error)] || mutationErrorMessage(error);
}

/** A stale read — the caller has to refetch before anything else will land. */
export function isVersionConflict(error: unknown): boolean {
  return seatingErrorCode(error) === 'version_conflict';
}
